
/* global BigInt */


// Mirrored in front/quoteFinancialInput.js: separate repositories have no shared
// runtime package. Parity tests exercise both adapters against the real engine.
const inputError = (code, message, field) => Object.assign(new Error(message), {
  status: 400, code, details: { field },
});

const assertEuro = (currency = "EUR") => {
  if (currency !== "EUR") throw inputError("QUOTE_CURRENCY_UNSUPPORTED",
    `Devise ${String(currency)} non prise en charge : seul EUR est autorisé en V1. Les montants historiques sont conservés sans conversion.`, "currencyCode");
  return currency;
};

const nonNegativeDecimal = (value, field, code) => {
  const normalized = typeof value === "string" ? value.trim().replace(",", ".") : value;
  if (!["number", "string"].includes(typeof normalized) || normalized === ""
    || !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(String(normalized))
    || !Number.isFinite(Number(normalized)) || Number(normalized) < 0) {
    throw inputError(code, `Valeur numérique positive ou nulle requise : ${field}.`, field);
  }
  return Number(normalized);
};

// The material snapshots and commercial discounts are DECIMAL(14,4).
// Validate explicit values before an ORM/SQL rounding could change the input.
const scaledFour = (value, field, code) => {
  const numeric = nonNegativeDecimal(value, field, code);
  const normalized = String(value).trim().replace(",", ".");
  if (numeric >= 10000000000 || /\.\d{4}\d*[1-9]\d*$/.test(normalized)) {
    throw inputError(code, `Précision de ${field} invalide : au plus quatre décimales.`, field);
  }
  const [integer, fraction = ""] = normalized.split(".");
  return BigInt(integer || "0") * 10000n + BigInt(fraction.padEnd(4, "0").slice(0, 4));
};

const materialNetUnitCost = (item) => {
  const code = "QUOTE_MATERIAL_COST_INVALID";
  if (item.currencyCodeSnapshot !== undefined) assertEuro(item.currencyCodeSnapshot);
  // NULL explicitly requests the catalogue fallback; empty text is invalid.
  if (item.netUnitPurchasePrice !== undefined && item.netUnitPurchasePrice !== null) {
    return Number(scaledFour(item.netUnitPurchasePrice, "netUnitPurchasePrice", code)) / 10000;
  }
  const gross = item.purchasePriceMode === "forced" ? item.forcedUnitPurchasePrice
    : item.catalogUnitPurchasePrice ?? item.unitPurchasePrice;
  const discount = item.discountMode === "forced" ? item.forcedDiscountRate : item.discountRate;
  const rate = discount == null ? 0n : scaledFour(discount, "discountRate", code);
  if (rate > 1000000n) throw inputError(code, "La remise fournisseur doit être comprise entre 0 et 100 %.", "discountRate");
  const price = gross == null ? 0n : scaledFour(gross, "catalogUnitPurchasePrice", code);
  // Round the newly derived snapshot once, at its existing storage precision
  // (half up), before both previews and writes; never adjust quote totals.
  return Number((price * (1000000n - rate) + 500000n) / 1000000n) / 10000;
};

const normalizeLineDiscount = (line) => {
  const code = "QUOTE_LINE_DISCOUNT_INVALID";
  const value = line.discountValue !== undefined ? line.discountValue : line.discount_value;
  const kind = line.discountKind !== undefined ? line.discountKind : line.discount_kind;
  if (kind != null && !["percent", "amount"].includes(kind)) {
    throw inputError(code, "Type de remise de ligne invalide : percent ou amount attendu.", "discountKind");
  }
  if (value === undefined || value === null) return { discountKind: kind ?? null, discountValue: null };
  const numeric = nonNegativeDecimal(value, "discountValue", code);
  // Same precision as the proposed DECIMAL(14,4); refuse instead of truncating.
  const normalized = String(value).trim().replace(",", ".");
  if (numeric >= 10000000000 || /\.\d{4}\d*[1-9]\d*$/.test(normalized)
    || ((kind ?? "percent") === "percent" && numeric > 100)) {
    throw inputError(code, "Remise invalide : 0 à 100 % ou montant positif, au plus quatre décimales.", "discountValue");
  }
  return { discountKind: kind ?? "percent", discountValue: numeric };
};

export { assertEuro, materialNetUnitCost, normalizeLineDiscount };
